import { computed, ref } from "vue";
import axios from "axios";

interface BounceRecommendation {
    type: "success" | "warning" | "error";
    message: string;
}

interface BounceAnalytics {
    reputation_score?: number;
    reputation_status?: "excellent" | "good" | "fair" | "poor";
    hard_bounce_rate?: number;
    soft_bounce_rate?: number;
    complaint_rate?: number;
    problem_contacts_count?: number;
    recommendations?: BounceRecommendation[];
}

const useBounceAnalytics = () => {
    const bounceData = ref<BounceAnalytics | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);

    const fetchBounceAnalytics = async (period = 30) => {
        loading.value = true;
        error.value = null;

        try {
            // Use the reputation health endpoint which contains the most relevant data
            const response = await axios.get("/api/bounce-reputation", {
                params: { period }
            });

            const data = response.data;
            bounceData.value = {
                reputation_score: data.score,
                reputation_status: data.status,
                hard_bounce_rate: data.hard_bounce_rate || 0,
                soft_bounce_rate: data.soft_bounce_rate || 0,
                complaint_rate: data.complaint_rate || 0,
                problem_contacts_count: data.problem_contacts_count || 0,
                recommendations: data.recommendations || []
            };
        } catch (err) {
            if (err instanceof Error) {
                error.value = err.message || "Failed to fetch bounce analytics";
            } else {
                error.value = "Failed to fetch bounce analytics";
            }
            console.error("Bounce analytics fetch error:", err);
        } finally {
            loading.value = false;
        }
    };

    return {
        bounceData: computed(() => bounceData.value),
        loading: computed(() => loading.value),
        error: computed(() => error.value),
        fetchBounceAnalytics
    };
};

export default useBounceAnalytics;
